import { call, refreshSession, WITH_SESSION_COOKIE } from '../api';
import { getSession, startSession, type Session } from './sessionStore';

/**
 * Picking the session back up after a reload.
 *
 * One request, once, at boot: `POST /auth/refresh` with the cookie attached. It
 * either comes back with everything signing in returns -- an access token, an
 * expiry and the resolved principal -- or it comes back `401`. There is no
 * third answer and nothing to retry.
 *
 * **A failure here is not an error.** A first visit, an expired session and a
 * revoked one all produce the same `401`, deliberately (see
 * `app/auth/refresh.py`), and the only sensible response to all three is the
 * login form with nothing red on it. Somebody who has never been here must not
 * be told their session ended.
 *
 * **It happens once per page load, not once per component.** The promise is
 * memoised, so React mounting the tree twice -- which `StrictMode` does in
 * development, on purpose -- spends one refresh token rather than two. That is
 * not a micro-optimisation: the second use of a rotated token is read by the
 * server as a stolen cookie and revokes the session, so a double refresh would
 * sign the person out on every reload.
 */

export type RestoreStatus = 'pending' | 'settled';

let attempt: Promise<Session | null> | null = null;
let status: RestoreStatus = 'pending';
const listeners = new Set<() => void>();

function settle(): void {
  status = 'settled';
  for (const listener of listeners) {
    listener();
  }
}

async function exchangeTheCookie(): Promise<Session | null> {
  try {
    const token = await call(refreshSession(WITH_SESSION_COOKIE));
    const session: Session = {
      accessToken: token.access_token,
      expiresAt: new Date(token.expires_at),
      principal: token.principal,
    };
    startSession(session);
    return session;
  } catch {
    // Every way of having no session looks like this, and all of them mean the
    // same thing to the person: the login form.
    return null;
  } finally {
    settle();
  }
}

/**
 * Start the one attempt, or hand back the one already in flight.
 *
 * Someone already signed in has nothing to restore -- they signed in a moment
 * ago, or a test seeded the session -- so the question is answered without a
 * request. Spending a refresh token to be told what is already in memory would
 * be one rotation for nothing.
 */
export function restoreSession(): Promise<Session | null> {
  const existing = getSession();
  if (existing !== null) {
    if (status === 'pending') {
      settle();
    }
    return Promise.resolve(existing);
  }
  attempt ??= exchangeTheCookie();
  return attempt;
}

export function getRestoreStatus(): RestoreStatus {
  return status;
}

export function subscribeToRestore(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Forget that a page load ever happened. For tests, which live many of them. */
export function resetRestore(): void {
  attempt = null;
  status = 'pending';
  for (const listener of listeners) {
    listener();
  }
}
